import JSZip from 'jszip';
import { ALL_POSITIONS, GameState, PlayerPosition, PokerAction, RangeDataset, RangeFile } from './types';
import { PokerGame } from './game';
import { TableRenderer } from './renderer';
import { DEAL_ANIMATION_MAX_MS } from './shared_constants';
import { createHostedRangeResolver, createMapRangeResolver, loadRangeDataset, buildFileMapFromRecords } from './range-loader';

// DOM elements
const startButton = document.getElementById('start-button') as HTMLButtonElement;
const userPositionSelect = document.getElementById('user-position') as HTMLSelectElement;
const drillOpenCheck = document.getElementById('drill-open') as HTMLInputElement;
const drillCallCheck = document.getElementById('drill-call') as HTMLInputElement;
const drill3betCheck = document.getElementById('drill-3bet') as HTMLInputElement;
const drill4betCheck = document.getElementById('drill-3bet') as HTMLInputElement;
const drillAllinCheck = document.getElementById('drill-allin') as HTMLInputElement;
const rangeSourceRadios = document.querySelectorAll('input[name="range-source"]') as NodeListOf<HTMLInputElement>;
const defaultRangeSettings = document.getElementById('default-range-settings') as HTMLDivElement;
const localFolderSettings = document.getElementById('local-folder-settings') as HTMLDivElement;
const zipUploadSettings = document.getElementById('zip-upload-settings') as HTMLDivElement;
const rangeFolderInput = document.getElementById('range-folder-input') as HTMLInputElement;
const rangeZipInput = document.getElementById('range-zip-input') as HTMLInputElement;
const rangeFolderStatus = document.getElementById('range-folder-status') as HTMLDivElement;
const rangeZipStatus = document.getElementById('range-zip-status') as HTMLDivElement;
const rangeLoadStatus = document.getElementById('range-load-status') as HTMLDivElement;
const handRangeSlider = document.getElementById('hand-range-slider') as HTMLInputElement;
const actionButtons = document.getElementById('action-buttons') as HTMLDivElement;
const correctCountEl = document.getElementById('correct-count') as HTMLSpanElement;
const totalCountEl = document.getElementById('total-count') as HTMLSpanElement;
const accuracyEl = document.getElementById('accuracy') as HTMLSpanElement;
const currentRoundEl = document.getElementById('rounds-completed') as HTMLSpanElement;
const roundsTotalEl = document.getElementById('rounds-total') as HTMLSpanElement;
const settingsToggle = document.getElementById('settings-toggle') as HTMLButtonElement;
const settingsPanel = document.getElementById('settings-panel') as HTMLDivElement;
const closeSettings = document.getElementById('close-settings') as HTMLButtonElement;
const advancedToggle = document.querySelector('.advanced-toggle') as HTMLDivElement;
const advancedContent = document.querySelector('.advanced-content') as HTMLDivElement;
const customRoundsInput = document.getElementById('custom-rounds') as HTMLInputElement;
const roundButtons = document.querySelectorAll('.round-button') as NodeListOf<HTMLButtonElement>;
const speedButtons = document.querySelectorAll('.speed-button') as NodeListOf<HTMLButtonElement>;
const customSpeedInput = document.getElementById('custom-speed') as HTMLInputElement;
const endResultsPopup = document.getElementById('results-popup') as HTMLDivElement;
const popupAccuracyEl = document.getElementById('popup-accuracy') as HTMLSpanElement;
const popupCorrectEl = document.getElementById('popup-correct') as HTMLSpanElement;
const popupTotalEl = document.getElementById('popup-total') as HTMLSpanElement;
const popupRoundsEl = document.getElementById('popup-rounds') as HTMLSpanElement;
const restartButton = document.getElementById('restart-button') as HTMLButtonElement;// Deal animation duration
const dealAnimationDurationInput = document.getElementById('deal-animation-duration') as HTMLInputElement;
const DEFAULT_DEAL_ANIMATION_DURATION = 1200; // ms
const DEFAULT_ROUNDS_TOTAL = 10;
const DEFAULT_PLAYER_ACTION_SPEED = 200; // ms
const BUILT_IN_RANGE_BASE = './json/';

type RangeSourceType = 'default' | 'folder' | 'zip';

// Action buttons
const foldButton = document.getElementById('action-fold') as HTMLButtonElement;
const callButton = document.getElementById('action-call') as HTMLButtonElement;
const raiseButton = document.getElementById('action-raise') as HTMLButtonElement;
const allinButton = document.getElementById('action-allin') as HTMLButtonElement;

hideActionButtons();

// Game state
let game: PokerGame;
let renderer: TableRenderer;

// Initialize the renderer and draw the empty table
renderer = new TableRenderer('poker-table');
renderer.drawEmptyTable();

let selectedRangeSource: RangeSourceType = 'default';
let folderRangeFileMap: Map<string, RangeFile> | null = null;
let zipRangeFileMap: Map<string, RangeFile> | null = null;
let folderSelectionSignature = '';
let zipSelectionSignature = '';
let cachedRangeDataset: RangeDataset | null = null;
let cachedRangeSignature: string | null = null;
let folderFileCount = 0;
let zipFileCount = 0;

// Set up button event listeners
startButton.addEventListener('click', startTraining);
foldButton.addEventListener('click', () => makeUserAction('F'));
callButton.addEventListener('click', () => makeUserAction('C'));
raiseButton.addEventListener('click', () => makeUserAction('R3'));
allinButton.addEventListener('click', () => makeUserAction('RAI'));
restartButton.addEventListener('click', () => {
   endResultsPopup.style.display = 'none';
   startTraining();
});

rangeSourceRadios.forEach(radio => {
   radio.addEventListener('change', () => {
      if (!radio.checked) {
         return;
      }
      selectedRangeSource = radio.value as RangeSourceType;
      updateRangeSourceVisibility(selectedRangeSource);
      saveSettings();
   });
});

rangeFolderInput.addEventListener('change', () => { void handleFolderSelection(); });
rangeZipInput.addEventListener('change', () => { void handleZipSelection(); });

updateRangeSourceVisibility(selectedRangeSource);
setStatus(rangeFolderStatus, 'No folder selected.');
setStatus(rangeZipStatus, 'No zip selected.');
updateRangeLoadStatus();

// Set up rounds buttons
roundButtons.forEach(button => {
   button.addEventListener('click', () => {
      // Remove active class from all buttons
      roundButtons.forEach(btn => btn.classList.remove('active'));
      // Add active class to clicked button
      button.classList.add('active');
      // Set the rounds value
      const rounds = parseInt(button.dataset.rounds || '20', 10);
      customRoundsInput.value = rounds.toString();
      if (game) {
         game.setRoundsTotal(rounds);
      }
      roundsTotalEl.textContent = rounds.toString();
   });
});

// Set up speed buttons
speedButtons.forEach(button => {
   button.addEventListener('click', () => {
      // Remove active class from all buttons
      speedButtons.forEach(btn => btn.classList.remove('active'));
      // Add active class to clicked button
      button.classList.add('active');
      // Set the speed value
      const speed = parseInt(button.dataset.speed || '100', 10);
      customSpeedInput.value = speed.toString();
      if (game) {
         game.setPlayerActionSpeed(speed);
      }
   });
});

// Custom speed input
customSpeedInput.addEventListener('change', () => {
   let customSpeed = parseInt(customSpeedInput.value, 10);
   if (customSpeed) {
      customSpeed = Math.max(10, Math.min(1000, customSpeed));
      // Remove active class from all buttons
      speedButtons.forEach(btn => btn.classList.remove('active'));
      if (game) {
         game.setPlayerActionSpeed(customSpeed);
      }
   }
});

const defaultRoundButton = document.querySelector('.round-button[data-rounds="20"]') as HTMLButtonElement;
if (defaultRoundButton) {
   defaultRoundButton.classList.add('active');
}

// Custom rounds input
customRoundsInput.addEventListener('change', () => {
   const customRounds = parseInt(customRoundsInput.value, 10);
   if (customRounds && customRounds > 0) {
      // Remove active class from all buttons
      roundButtons.forEach(btn => btn.classList.remove('active'));
      if (game) {
         game.setRoundsTotal(customRounds);
      }
      roundsTotalEl.textContent = customRounds.toString();
   }
});
// Hand range slider 
handRangeSlider.addEventListener('change', () => {
   const rangeWidth = parseInt(handRangeSlider.value, 10);
   if (game) {
      game.setHandRangeWidth(rangeWidth);
   }
   saveSettings();
});

// Toggle settings panel
settingsToggle.addEventListener('click', () => {
   if (settingsPanel.style.display === 'block') {
      settingsPanel.style.display = 'none';
      // Show start training button in action buttons area if training hasn't begun
      if (!game) {
         hideActionButtons();

         // Create Start Training button if it doesn't exist
         let startTrainingButton = document.getElementById('start-training-button') as HTMLButtonElement;
         if (!startTrainingButton) {
            startTrainingButton = document.createElement('button');
            startTrainingButton.id = 'start-training-button';
            startTrainingButton.textContent = 'Start Training';
            startTrainingButton.addEventListener('click', startTraining);
            actionButtons.appendChild(startTrainingButton);
         }
         startTrainingButton.style.display = 'block';
      }
   } else {
      settingsPanel.style.display = 'block';
   }
});

function closeSettingsPanel() {
   settingsPanel.style.display = 'none';

   if (!game) {
      hideActionButtons();

      let startTrainingButton = document.getElementById('start-training-button') as HTMLButtonElement;
      if (!startTrainingButton) {
         startTrainingButton = document.createElement('button');
         startTrainingButton.id = 'start-training-button';
         startTrainingButton.textContent = 'Start Training';
         startTrainingButton.addEventListener('click', startTraining);
         actionButtons.appendChild(startTrainingButton);
      }
      startTrainingButton.style.display = 'block';
   }
}

closeSettings.addEventListener('click', closeSettingsPanel);

// Add keyboard shortcuts for actions and closing settings
document.addEventListener('keydown', (event) => {
   // ESC key closes settings menu
   if (event.key === 'Escape' && settingsPanel.style.display === 'block') {
      settingsPanel.style.display = 'none';

      // Show start training button in action buttons area if training hasn't begun
      if (!game) {
         hideActionButtons();

         // Create Start Training button if it doesn't exist
         let startTrainingButton = document.getElementById('start-training-button') as HTMLButtonElement;
         if (!startTrainingButton) {
            startTrainingButton = document.createElement('button');
            startTrainingButton.id = 'start-training-button';
            startTrainingButton.textContent = 'Start Training';
            startTrainingButton.addEventListener('click', startTraining);
            actionButtons.appendChild(startTrainingButton);
         }
         startTrainingButton.style.display = 'block';
      }
      return;
   }

   if (endResultsPopup.style.display !== 'none') {
      if (event.key === 'Enter' || event.key === ' ') {
         endResultsPopup.style.display = 'none';
         startTraining();
      }
   }

   if (settingsPanel.style.display !== 'none') {
      if (event.key === 'Enter' || event.key === ' ') {
         startTraining();
      }
   }

   // Check if the hand complete dialog is visible
   const handCompleteDialog = document.getElementById('hand-complete-dialog');
   if (handCompleteDialog && handCompleteDialog.style.display !== 'none') {
      if (event.key === 'Enter' || event.key === ' ') {
         document.dispatchEvent(new CustomEvent('next-hand-request'));
      }
   }

   // Only handle action shortcuts when game is active and it's user's turn
   if (game && actionButtons.style.display === 'flex' && 
      foldButton.style.display !== 'none' && !event.ctrlKey && !event.altKey && !event.metaKey) {
      // z: all-in
      if (event.key === 'z' || event.key === 'Z') {
         makeUserAction('RAI');
         event.preventDefault();
      }
      // c: call
      else if (event.key === 'c' || event.key === 'C') {
         makeUserAction('C');
         event.preventDefault();
      }
      // x: fold/check
      else if (event.key === 'x' || event.key === 'X' || event.key === 'f' || event.key === 'F') {
         makeUserAction('F');
         event.preventDefault();
         if (event.key === 'f' || event.key === 'F') {
            document.getElementById('action-fold')!.innerHTML = 'Fold <kbd>f</kbd>';
         } else {
            document.getElementById('action-fold')!.innerHTML = 'Fold <kbd>x</kbd>';
         }
      }
      // 1: raise
      else if (event.key === '1') {
         makeUserAction('R3');
         event.preventDefault();
      }
   }
});

// Close settings when clicking outside the panel
document.addEventListener('click', (event) => {
   if (
      !settingsPanel.contains(event.target as Node) && 
         event.target !== settingsToggle && 
         settingsPanel.style.display === 'block'
   ) {
      settingsPanel.style.display = 'none';

      // Show start training button in action buttons area if training hasn't begun
      if (!game) {

         hideActionButtons();

         // Create Start Training button if it doesn't exist
         let startTrainingButton = document.getElementById('start-training-button') as HTMLButtonElement;
         if (!startTrainingButton) {
            startTrainingButton = document.createElement('button');
            startTrainingButton.id = 'start-training-button';
            startTrainingButton.textContent = 'Start Training';
            startTrainingButton.addEventListener('click', startTraining);
            actionButtons.appendChild(startTrainingButton);
         }
         startTrainingButton.style.display = 'block';
      }
   }
});

// Listen for the show-results event from game-controller
document.addEventListener('show-results', () => {
   console.log("showEndResultsPopup() 'show-results' Event");
   showEndResultsPopup();
});

// Listen for the next-hand-request event from renderer
document.addEventListener('next-hand-request', () => {
   if (game) {
      game.nextHandPlease();
   }
});

// Toggle advanced settings
advancedToggle.addEventListener('click', () => {
   const isOpen = advancedContent.style.display === 'block';
   advancedContent.style.display = isOpen ? 'none' : 'block';
   advancedToggle.classList.toggle('open', !isOpen);
   advancedToggle.querySelector('.toggle-icon')!.textContent = isOpen ? '+' : '×';
});

// Load saved settings from local storage
function loadSettings(): void {
   try {
      const savedSettings = localStorage.getItem('pokerTrainerSettings');
      if (savedSettings) {
         const settings = JSON.parse(savedSettings);

         console.log(settings);

         if (settings.userPosition) userPositionSelect.value = settings.userPosition;
         if (settings.drillOpen !== undefined) drillOpenCheck.checked = settings.drillOpen;
         // if (settings.drillCall !== undefined) drillCallCheck.checked = settings.drillCall; TODO: add back
         if (settings.drill3bet !== undefined) drill3betCheck.checked = settings.drill3bet;
         if (settings.drill4bet !== undefined) drill4betCheck.checked = settings.drill4bet;
         // if (settings.drillAllin !== undefined) drillAllinCheck.checked = settings.drillAllin; TODO: add back
         const storedSource = settings.rangeSourceType as RangeSourceType | undefined;
         setRangeSource(storedSource ?? 'default');

         if (settings.rangeSourceType === 'folder') {
            setStatus(rangeFolderStatus, 'Select the folder again to reload ranges.');
         } else if (settings.rangeSourceType === 'zip') {
            setStatus(rangeZipStatus, 'Upload the zip again to reload ranges.');
         }

         // Load rounds settings
         if (settings.roundsTotal) {
            const roundsTotal = settings.roundsTotal;
            customRoundsInput.value = roundsTotal.toString();
            roundsTotalEl.textContent = roundsTotal.toString();

            // Find and activate the matching button if it exists
            roundButtons.forEach(btn => {
               if (btn.dataset.rounds === settings.roundsTotal.toString()) {
                  btn.classList.add('active');
               } else {
                  btn.classList.remove('active');
               }
            });
         }
         
         // Load speed settings
         if (settings.playerActionSpeed) {
            const playerActionSpeed = settings.playerActionSpeed;
            customSpeedInput.value = playerActionSpeed.toString();
            
            // Find and activate the matching button if it exists
            speedButtons.forEach(btn => {
               if (btn.dataset.speed === playerActionSpeed.toString()) {
                  btn.classList.add('active');
               } else {
                  btn.classList.remove('active');
               }
            });
         }
         
         // Load hand range width setting
         if (settings.handRangeWidth !== undefined) {
            handRangeSlider.value = settings.handRangeWidth.toString();
         }
         
         // Load deal animation duration setting
         if (settings.dealAnimationDuration !== undefined) {
            dealAnimationDurationInput.value = settings.dealAnimationDuration.toString();
         }
      }
   } catch (error) {
      console.error('Error loading settings:', error);
   }
}

// Save settings to local storage
function saveSettings(): void {
   try {
      const roundsTotal = parseInt(customRoundsInput.value, 10) || DEFAULT_ROUNDS_TOTAL;
      const playerActionSpeed = parseInt(customSpeedInput.value, 10) || DEFAULT_PLAYER_ACTION_SPEED;
      const handRangeWidth = handRangeSlider.value === '' ? 50 : parseInt(handRangeSlider.value, 10);
      const dealAnimationDuration = parseInt(dealAnimationDurationInput.value, 10) || DEFAULT_DEAL_ANIMATION_DURATION;
      
      const settings = {
         userPosition: userPositionSelect.value,
         drillOpen: drillOpenCheck.checked,
         drillCall: drillCallCheck.checked,
         drill3bet: drill3betCheck.checked,
         drillAllin: drillAllinCheck.checked,
         rangeSourceType: selectedRangeSource,
         roundsTotal: roundsTotal,
         playerActionSpeed: playerActionSpeed,
         handRangeWidth: handRangeWidth,
         dealAnimationDuration: dealAnimationDuration
      };

      localStorage.setItem('pokerTrainerSettings', JSON.stringify(settings));
   } catch (error) {
      console.error('Error saving settings:', error);
   }
}

// Deal animation duration input handler
dealAnimationDurationInput.addEventListener('change', () => {
   let duration = parseInt(dealAnimationDurationInput.value, 10);
   if (duration) {
      duration = Math.max(100, Math.min(DEAL_ANIMATION_MAX_MS, duration));
      dealAnimationDurationInput.value = duration.toString();
      if (game) {
         game.setDealAnimationDuration(duration);
      }
      saveSettings();
   }
});

// Load settings on page load
loadSettings();
// startTraining(); // TEMP DEV DEL ME

// async function initialize(): Promise<void> {
//    await game.loadRangeData();
// }

function clearRangeCache(): void {
   cachedRangeDataset = null;
   cachedRangeSignature = null;
   setStatus(rangeLoadStatus, 'Range data cleared. Start training to reload.');
}

function updateRangeLoadStatus(): void {
   switch (selectedRangeSource) {
      case 'default':
         setStatus(rangeLoadStatus, 'Default range data ready.');
         break;
      case 'folder':
         if (folderFileCount > 0) {
            setStatus(rangeLoadStatus, `Loaded ${folderFileCount} JSON files from folder.`);
         } else {
            setStatus(rangeLoadStatus, 'Select a folder to load custom ranges.');
         }
         break;
      case 'zip':
         if (zipFileCount > 0) {
            setStatus(rangeLoadStatus, `Loaded ${zipFileCount} JSON files from zip.`);
         } else {
            setStatus(rangeLoadStatus, 'Upload a zip file to load custom ranges.');
         }
         break;
   }
}

function updateRangeSourceVisibility(source: RangeSourceType): void {
   defaultRangeSettings.style.display = source === 'default' ? 'block' : 'none';
   localFolderSettings.style.display = source === 'folder' ? 'block' : 'none';
   zipUploadSettings.style.display = source === 'zip' ? 'block' : 'none';
}

function setRangeSource(source: RangeSourceType): void {
   selectedRangeSource = source;
   rangeSourceRadios.forEach(radio => {
      radio.checked = radio.value === source;
   });
   updateRangeSourceVisibility(source);
   updateRangeLoadStatus();
}

function getCurrentRangeSignature(): string | null {
   switch (selectedRangeSource) {
      case 'default':
         return 'default';
      case 'folder':
         return folderSelectionSignature || null;
      case 'zip':
         return zipSelectionSignature || null;
      default:
         return null;
   }
}

async function loadDatasetForCurrentSource(): Promise<RangeDataset> {
   if (selectedRangeSource === 'default') {
      const resolver = createHostedRangeResolver(BUILT_IN_RANGE_BASE);
      return loadRangeDataset(resolver);
   }

   if (selectedRangeSource === 'folder') {
      if (!folderRangeFileMap) {
         throw new Error('Select a folder that contains range JSON files.');
      }
      const resolver = createMapRangeResolver(folderRangeFileMap);
      return loadRangeDataset(resolver);
   }

   if (!zipRangeFileMap) {
      throw new Error('Upload a range zip file before starting training.');
   }
   const resolver = createMapRangeResolver(zipRangeFileMap);
   return loadRangeDataset(resolver);
}

async function ensureRangeDatasetLoaded(): Promise<RangeDataset> {
   const signature = getCurrentRangeSignature();
   if (!signature) {
      throw new Error('Provide range data for the selected source.');
   }

   if (cachedRangeDataset && cachedRangeSignature === signature) {
      return cachedRangeDataset;
   }

   const dataset = await loadDatasetForCurrentSource();
   cachedRangeDataset = dataset;
   cachedRangeSignature = signature;
   return dataset;
}

function setStatus(element: HTMLDivElement, message: string, isError = false): void {
   element.textContent = message;
   element.classList.toggle('error', isError);
   element.style.display = message ? 'block' : 'none';
}

async function handleFolderSelection(): Promise<void> {
   const files = rangeFolderInput.files;
   if (!files || files.length === 0) {
      folderRangeFileMap = null;
      folderSelectionSignature = '';
      folderFileCount = 0;
      setStatus(rangeFolderStatus, 'No folder selected.');
      updateRangeLoadStatus();
      return;
   }

   setStatus(rangeFolderStatus, 'Reading folder...');
   try {
      const records = await readRecordsFromFileList(files);
      folderRangeFileMap = buildFileMapFromRecords(records, 'folder');
      folderSelectionSignature = `folder:${Date.now()}:${records.length}`;
      clearRangeCache();
      folderFileCount = records.length;
      setStatus(rangeFolderStatus, `Loaded ${records.length} JSON files.`);
      updateRangeLoadStatus();
   } catch (error) {
      folderRangeFileMap = null;
      folderSelectionSignature = '';
      folderFileCount = 0;
      const message = `Folder load failed: ${(error as Error).message}`;
      setStatus(rangeFolderStatus, message, true);
      setStatus(rangeLoadStatus, message, true);
   }
}

async function handleZipSelection(): Promise<void> {
   const file = rangeZipInput.files?.[0];
   if (!file) {
      zipRangeFileMap = null;
      zipSelectionSignature = '';
      zipFileCount = 0;
      setStatus(rangeZipStatus, 'No zip selected.');
      updateRangeLoadStatus();
      return;
   }

   setStatus(rangeZipStatus, 'Reading zip...');
   try {
      const zip = await JSZip.loadAsync(file);
      const entries: Array<{ path: string; data: unknown }> = [];
      const zipFiles = Object.values(zip.files);
      for (const entry of zipFiles) {
         if (entry.dir || !entry.name.toLowerCase().endsWith('.json')) {
            continue;
         }
         const text = await entry.async('string');
         entries.push({ path: entry.name, data: JSON.parse(text) });
      }

      if (entries.length === 0) {
         throw new Error('Zip does not contain any JSON files.');
      }

      zipRangeFileMap = buildFileMapFromRecords(entries, file.name);
      zipSelectionSignature = `zip:${file.name}:${Date.now()}:${entries.length}`;
      clearRangeCache();
      zipFileCount = entries.length;
      setStatus(rangeZipStatus, `Loaded ${entries.length} JSON files.`);
      updateRangeLoadStatus();
   } catch (error) {
      zipRangeFileMap = null;
      zipSelectionSignature = '';
      zipFileCount = 0;
      const message = `Zip load failed: ${(error as Error).message}`;
      setStatus(rangeZipStatus, message, true);
      setStatus(rangeLoadStatus, message, true);
   }
}

async function readRecordsFromFileList(fileList: FileList): Promise<Array<{ path: string; data: unknown }>> {
   const entries: Array<{ path: string; data: unknown }> = [];
   for (const file of Array.from(fileList)) {
      if (!file.name.toLowerCase().endsWith('.json')) {
         continue;
      }
      const text = await file.text();
      const relativePath = file.webkitRelativePath || file.name;
      entries.push({ path: relativePath, data: JSON.parse(text) });
   }
   if (entries.length === 0) {
      throw new Error('No JSON files found in the selected folder.');
   }
   return entries;
}

async function startTraining(): Promise<void> {
   const userPositionValue = userPositionSelect.value;
   const userPosition: PlayerPosition | null = ALL_POSITIONS.includes(userPositionValue as PlayerPosition) ? (userPositionValue as PlayerPosition) : null;

   // Determine which actions to drill
   const actionsToDrill: PokerAction[] = [];
   if (drillOpenCheck.checked) actionsToDrill.push('F');
   if (drillCallCheck.checked) actionsToDrill.push('C');
   if (drill3betCheck.checked) actionsToDrill.push('R3');
   if (drillAllinCheck.checked) actionsToDrill.push('RAI');

   // Save current settings to local storage
   saveSettings();

   // Stats will be initialized in the game object
   updateStatsDisplay();

   // Hide results popup if it's open
   endResultsPopup.style.display = 'none';

   // Remove start training button if it exists
   const startTrainingButton = document.getElementById('start-training-button');
   if (startTrainingButton) {
      startTrainingButton.remove();
   }

   // Close settings panel
   settingsPanel.style.display = 'none';

   try {
      // Initialize the game
      game = new PokerGame(
         userPosition,
         actionsToDrill,
         updateGameDisplay
      );

      console.log("Loading range data...");
      setStatus(rangeLoadStatus, 'Loading range data...');
      const dataset = await ensureRangeDatasetLoaded();
      setStatus(rangeLoadStatus, 'Range data loaded successfully.');
      game.setRangeData(dataset.ranges, dataset.raiseRanges, dataset.fourBetRanges);
      
      // Set player action speed from settings
      const playerActionSpeed = parseInt(customSpeedInput.value, 10) || 100;
      game.setPlayerActionSpeed(playerActionSpeed);
      
      // Set hand range width from slider
      const handRangeWidth = handRangeSlider.value === '' ? 50 : parseInt(handRangeSlider.value, 10);
      game.setHandRangeWidth(handRangeWidth);
      
      // Set deal animation duration
      const dealAnimationDuration = parseInt(dealAnimationDurationInput.value, 10) || DEFAULT_DEAL_ANIMATION_DURATION;
      game.setDealAnimationDuration(dealAnimationDuration);

      // Reset round stats and ensure round number starts at 1
      game.resetRoundStats(+(roundsTotalEl.textContent ?? 20));
      game.setRoundNumber(1);
      game.startNewHand();

      // Show action buttons
      actionButtons.style.display = 'flex';

      // Change start button to "Next Hand"
      // startButton.style.display = 'none';
      startButton.textContent = 'Restart Training';
      // startButton.removeEventListener('click', startTraining);
      // startButton.addEventListener('click', () => { closeSettingsPanel(); return nextHand });

      // updateGameDisplay(game.);

   } catch (error) {
      console.error('Failed to start training:', error);
      setStatus(rangeLoadStatus, `Range load failed: ${(error as Error).message}`, true);
      return;
   }
}

function hideActionButtons() {
   foldButton.style.display = 'none';
   callButton.style.display = 'none';
   raiseButton.style.display = 'none';
   allinButton.style.display = 'none';
}

function showActionButtons() {
   foldButton.style.display = '';
   callButton.style.display = '';
   raiseButton.style.display = '';
   allinButton.style.display = '';
}

function handleNextHandButton() {
   game.nextHandPlease();
}

function handleShowResultsButton(gameState: GameState) {
   if (gameState.stats.roundNumber >= gameState.stats.roundsTotal) {
      // Show results instead of next hand button
      console.log("showEndResultsPopup() handleShowResultsButton");
      showEndResultsPopup();
   }
}

function updateGameDisplay(gameState: GameState): void {
   // console.log("updateGameDisplay()");
   
   // Make sure the renderer's animation duration matches the game's
   if (game) {
      renderer.setDealAnimationDuration(game.getDealAnimationDuration());
   }
   
   renderer.render(gameState);

   // Show/hide action buttons based on whether it's the user's turn
   const currentPlayer = gameState.players[gameState.currentPlayerIndex];
   const isUserTurn = currentPlayer && currentPlayer.isUser && !gameState.isHandComplete;

   if (gameState.isHandComplete) {
      // Show next hand button when hand is over
      hideActionButtons();

      // // Check if we've reached the total rounds TODO: do this after click
      // if (gameState.stats.roundsCompleted >= gameState.stats.roundsTotal) {
      //    // Show results instead of next hand button
      //    showResultsPopup();
      //    return;
      // }

      // Create Next Hand button if it doesn't exist
      let nextHandButton = document.getElementById('next-hand-button') as HTMLButtonElement;
      if (!nextHandButton) {
         nextHandButton = document.createElement('button');
         nextHandButton.id = 'next-hand-button';
         nextHandButton.textContent = 'Next Hand';
         nextHandButton.addEventListener('click', handleNextHandButton);
         actionButtons.appendChild(nextHandButton);
      }
      nextHandButton.style.display = 'block';
      if (gameState.stats.roundNumber >= gameState.stats.roundsTotal) {
         nextHandButton.removeEventListener('click', handleNextHandButton);
         nextHandButton.addEventListener('click', () => { handleShowResultsButton(gameState); });
         nextHandButton.textContent = 'Show Results!';
      }
   } else {
      // Normal gameplay state

      if (isUserTurn) {
         showActionButtons();
      } else {
         hideActionButtons();
      }

      // Hide Next Hand button if it exists
      const nextHandButton = document.getElementById('next-hand-button');
      if (nextHandButton) {
         nextHandButton.style.display = 'none';
      }
   }

   updateStatsDisplay();
}

function makeUserAction(action: PokerAction): void {
   if (!game) return;
   game.makeUserAction(action);
   updateStatsDisplay();
}

function showEndResultsPopup(): void {
   if (!game) return;
   
   const gameStats = game.getStats();
   
   // Update popup content
   popupCorrectEl.textContent = gameStats.correctDecisions.toString();
   popupTotalEl.textContent = gameStats.totalActions.toString();
   popupRoundsEl.textContent = (gameStats.roundNumber).toString();

   const accuracy = gameStats.totalActions === 0 
      ? 0 
      : Math.round((gameStats.correctDecisions / gameStats.totalActions) * 100);

   popupAccuracyEl.textContent = `${accuracy}%`;

   // Update the accuracy circle visualization
   const accuracyCircle = document.querySelector('.accuracy-circle') as HTMLElement;
   accuracyCircle.style.background = `conic-gradient(var(--button-hover) 0% ${accuracy}%, var(--panel-bg-lighter) ${accuracy}% 100%)`;

   // Show the popup
   endResultsPopup.style.display = 'block';
}

function updateStatsDisplay(): void {
   if (!game) {
      currentRoundEl.textContent = "1";
      roundsTotalEl.textContent = customRoundsInput.value || "20";
      return;
   }
   const gameStats = game.getStats();
   // correctCountEl.textContent = gameStats.correctDecisions.toString();
   // totalCountEl.textContent = gameStats.totalHands.toString();
   currentRoundEl.textContent = (gameStats.roundNumber).toString();
   roundsTotalEl.textContent = gameStats.roundsTotal.toString();

   // const accuracy = gameStats.totalHands === 0 
   //    ? 0 
   //    : Math.round((gameStats.correctDecisions / gameStats.totalHands) * 100);

   // accuracyEl.textContent = `${accuracy}%`;
}
